import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { TEAM } from "@/config/team";
import { useI18n } from "@/i18n/i18n";

const AUTOPLAY_MS = 6000;

export default function TeamCarousel({ compact = false }) {
  const { tr } = useI18n();
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);

  // Merge photos from config/team.js with translated labels from i18n
  const members = TEAM.map((m, i) => ({
    ...m,
    ...(tr.team.members[i] || {}),
  }));

  const goTo = (i) => setIndex(((i % members.length) + members.length) % members.length);
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  useEffect(() => {
    if (hovered) return;
    timerRef.current = setTimeout(() => goTo(index + 1), AUTOPLAY_MS);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, hovered]);

  const member = members[index];

  return (
    <section
      className={`relative overflow-hidden ${compact ? "py-20" : "py-24 lg:py-32"}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="team-carousel"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-96 w-[80%] rounded-full bg-[color:var(--mc-primary)]/15 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="overline text-[color:var(--mc-gold)] flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> {tr.team.overline}
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold tracking-tight mt-4 leading-tight text-white">
              {tr.team.title_pre}{" "}
              <em className="not-italic italic bg-gradient-to-r from-[color:var(--mc-cyan-2)] to-[color:var(--mc-cyan)] bg-clip-text text-transparent">
                {tr.team.title_em}
              </em>
            </h2>
            <p className="mt-5 text-white/70 text-lg leading-relaxed">{tr.team.subtitle}</p>
          </div>

          <div className="flex items-center gap-3" data-testid="team-carousel-controls">
            <button onClick={prev} className="h-12 w-12 rounded-full border border-white/15 bg-white/[0.04] hover:border-[color:var(--mc-cyan)] hover:text-[color:var(--mc-cyan)] flex items-center justify-center transition-colors text-white" aria-label="Previous" data-testid="team-prev">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={next} className="h-12 w-12 rounded-full border border-white/15 bg-white/[0.04] hover:border-[color:var(--mc-cyan)] hover:text-[color:var(--mc-cyan)] flex items-center justify-center transition-colors text-white" aria-label="Next" data-testid="team-next">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
          <div className="lg:col-span-7 relative h-[440px] sm:h-[560px] rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl shadow-blue-950/40">
            <AnimatePresence mode="wait">
              <motion.img
                key={member.id}
                src={member.image}
                alt={member.name}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: member.objectPosition || "50% 50%" }}
                data-testid={`team-image-${index}`}
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--mc-bg)]/85 via-[color:var(--mc-bg)]/15 to-transparent" />
            <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-black/40 backdrop-blur border border-white/15 px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--mc-gold)]" />
              <span className="overline text-white/90 text-[10px]">{member.accent}</span>
            </div>
            <div className="absolute bottom-5 right-5 flex items-center gap-1.5">
              {members.map((_, i) => (
                <button key={i} onClick={() => goTo(i)} className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-[color:var(--mc-gold)]" : "w-3 bg-white/35 hover:bg-white/60"}`} aria-label={`Go to slide ${i + 1}`} data-testid={`team-dot-${i}`} />
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 flex">
            <AnimatePresence mode="wait">
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.55, ease: "easeOut" }}
                className="mc-card p-8 lg:p-10 flex flex-col justify-between w-full"
                data-testid={`team-card-${index}`}
              >
                <div>
                  <p className="overline text-[color:var(--mc-cyan)]">
                    {String(index + 1).padStart(2, "0")} / {String(members.length).padStart(2, "0")}
                  </p>
                  <h3 className="font-serif-display text-3xl sm:text-4xl text-white mt-4 leading-[1.1]">{member.name}</h3>
                  <p className="mt-2 text-[color:var(--mc-gold)] font-medium tracking-wide">{member.role}</p>
                  <p className="mt-6 text-white/75 leading-relaxed">{member.bio}</p>
                </div>

                <div className="mt-10 pt-6 border-t border-white/10">
                  <p className="text-xs uppercase tracking-[0.22em] text-white/45 mb-4">{tr.team.complete_label}</p>
                  <div className="flex items-center gap-2">
                    {members.map((m, i) => (
                      <button key={m.id} onClick={() => goTo(i)} className={`relative h-12 w-12 rounded-full overflow-hidden border-2 transition-all ${i === index ? "border-[color:var(--mc-gold)] scale-110" : "border-white/15 opacity-60 hover:opacity-100"}`} aria-label={m.name} data-testid={`team-thumb-${i}`}>
                        <img src={m.image} alt={m.name} className="h-full w-full object-cover" style={{ objectPosition: m.objectPosition || "50% 30%" }} />
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
